import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useT } from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';
import { AiThinking } from '@/shared/ui/motion';
import { ApiError } from '@/shared/api/api-error';
import {
  sendAssistantMessage,
  type AssistantMessage,
} from '@/features/candidate-management/assistant/services/assistant.service';

interface Message {
  id: number;
  role: 'assistant' | 'user';
  text: string;
}

/**
 * AiAssistant — a global floating chat widget backed by the Workerize AI
 * assistant endpoint (Anthropic). Each user message consumes one AI credit;
 * when AI is disabled on the deployment the panel says so instead of faking a
 * reply. Pinned bottom-trailing, mirrors under RTL, respects reduced motion.
 */
export function AiAssistant(): ReactNode {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const nextId = (): number => (seq.current += 1);

  useEffect(() => {
    if (!open) return;
    setMessages((prev) =>
      prev.length === 0
        ? [{ id: nextId(), role: 'assistant', text: t('assistant.greeting') }]
        : prev,
    );
    inputRef.current?.focus();
  }, [open, t]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  async function send(event: FormEvent): Promise<void> {
    event.preventDefault();
    const text = input.trim();
    if (!text || busy) return;

    const userMessage: Message = { id: nextId(), role: 'user', text };
    const history = [...messages, userMessage];
    setMessages(history);
    setInput('');
    setBusy(true);

    // Build the API payload: real turns only, and it must start with a user
    // message (drop the seeded assistant greeting).
    const payload: AssistantMessage[] = history
      .map((m) => ({ role: m.role, content: m.text }))
      .filter((_, i) => !(i === 0 && history[0]?.role === 'assistant'));

    try {
      const result = await sendAssistantMessage(payload);
      const replyText = !result.enabled
        ? t('assistant.disabled')
        : result.reply || t('assistant.error');
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'assistant', text: replyText },
      ]);
    } catch (err) {
      const text402 =
        err instanceof ApiError && err.status === 402
          ? t('assistant.outOfCredits')
          : t('assistant.error');
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'assistant', text: text402 },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {open ? (
        <div
          role="dialog"
          aria-label={t('assistant.title')}
          className="fixed bottom-24 right-6 z-[60] flex h-[min(70vh,520px)] w-[min(92vw,380px)] origin-bottom-right flex-col overflow-hidden rounded-3xl border border-outline/15 bg-[var(--surface-base)] shadow-2xl transition-all duration-300 ease-standard rtl:left-6 rtl:right-auto rtl:origin-bottom-left"
        >
          <div className="relative flex items-center gap-3 overflow-hidden bg-[var(--ink-band)] px-5 py-4 text-[var(--on-ink-band)]">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-secondary-fixed/20 blur-2xl"
            />
            <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-secondary-fixed text-on-secondary-fixed">
              <Icon name="sparkles" size={20} />
            </span>
            <div className="relative flex-1">
              <p className="text-sm font-semibold leading-tight">{t('assistant.title')}</p>
              <p className="text-xs text-[var(--on-ink-band-variant)]">
                {t('assistant.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('assistant.close')}
              className="relative flex h-8 w-8 items-center justify-center rounded-full text-[var(--on-ink-band-variant)] transition-colors hover:bg-white/10 hover:text-[var(--on-ink-band)]"
            >
              <Icon name="close" size={18} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto bg-surface-container-low/40 p-4"
          >
            {messages.map((m) => (
              <div
                key={m.id}
                className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                    m.role === 'user'
                      ? 'rounded-br-md bg-[var(--text-primary)] text-[var(--surface-base)] rtl:rounded-bl-md rtl:rounded-br-2xl'
                      : 'rounded-bl-md border border-outline/10 bg-surface-container-lowest text-on-surface rtl:rounded-br-md rtl:rounded-bl-2xl',
                  )}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy ? (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md border border-outline/10 bg-surface-container-lowest px-4 py-2.5">
                  <AiThinking label={t('assistant.thinking')} />
                </div>
              </div>
            ) : null}
          </div>

          <form
            onSubmit={(e) => void send(e)}
            className="flex items-center gap-2 border-t border-outline/10 bg-[var(--surface-base)] p-3"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              aria-label={t('assistant.input.label')}
              placeholder={t('assistant.placeholder')}
              disabled={busy}
              className="min-w-0 flex-1 rounded-full bg-surface-container-low px-4 py-2.5 text-sm text-on-surface placeholder:text-outline/60 disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-secondary-fixed/50"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label={t('assistant.send')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-fixed text-on-secondary-fixed transition-all hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Icon name="arrow-up" size={18} />
            </button>
          </form>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('assistant.fab.label')}
        aria-expanded={open}
        data-testid="ai-assistant-fab"
        className={cn(
          'fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-secondary-fixed text-on-secondary-fixed shadow-lg shadow-secondary-fixed/30 transition-all duration-300 ease-standard hover:scale-110 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60 rtl:left-6 rtl:right-auto',
          open && 'rotate-90',
        )}
      >
        {open ? <Icon name="close" size={24} /> : <Icon name="sparkles" size={24} />}
      </button>
    </>
  );
}

export default AiAssistant;
